import _ from 'lodash';
import { MultiValue } from 'react-select';
import ICompany, { ICompanySummary, ITimeseries, IUserListRef } from '../types/company';

export type StringOption = { value: string; label: string };
export type NumberOption = { value: number; label: string };

const ADD_NEW_STRING = '+ Create new list';
export const ADD_NEW_VALUE = -1;

export const getCompanyListIds = (company: ICompanySummary): number[] => {
  return _.uniq((company.lists ?? []).map((list) => list.id)).filter((id) => typeof id === 'number' && id > 0);
};

export const convertToValueLabelStrings = (items: string[]): MultiValue<StringOption> => {
  const ordered = _.orderBy(_.uniq(items), [(u) => _.toLower(u)], ['asc']);
  const filtered = ordered.filter((item) => !!item && item !== 'null');
  return filtered.map((item) => ({ value: item, label: item }));
};

export const convertFromValueLabelStrings = (valueLabels: MultiValue<StringOption>): string[] => {
  const values = valueLabels.map((item) => item.value);
  return _.orderBy(_.uniq(values), [(u) => _.toLower(u)], ['asc']);
};

export const convertToValueLabelListIds = (listIds: number[], userLists: IUserListRef[]): MultiValue<NumberOption> => {
  const userListsById = userLists.reduce<Record<number, IUserListRef>>((acc, list) => {
    acc[list.id] = list;
    return acc;
  }, {});
  const uniqueIds = _.uniq(listIds).filter((id) => typeof id === 'number' && id > 0);
  const orderedIds = _.orderBy(uniqueIds, [(id) => _.toLower(userListsById[id]?.name ?? '')], ['asc']);
  return orderedIds.map((id) => ({ value: id, label: userListsById[id]?.name ?? `List ${id}` }));
};

export const convertFromValueLabelListIds = (valueLabels: MultiValue<NumberOption>): number[] => {
  return _.uniq(valueLabels.map((item) => item.value));
};

export const addNewOption = (initialList: MultiValue<NumberOption>): MultiValue<NumberOption> => {
  const existingObject = initialList.find((item) => item.value === ADD_NEW_VALUE);
  if (!existingObject) return [{ label: ADD_NEW_STRING, value: ADD_NEW_VALUE }, ...initialList];
  return initialList;
};

export const removeNewOption = (initialList: MultiValue<NumberOption>): MultiValue<NumberOption> => {
  return initialList.filter((item) => item.value !== ADD_NEW_VALUE);
};

const _getFilteredCompanies = (companies: ICompanySummary[], listId: number): ICompanySummary[] => {
  return companies.filter((company) => getCompanyListIds(company).includes(listId));
};

export const getUniqueCountries = (companies: ICompanySummary[], defaultListId: number | null): string[] => {
  let _companies = companies;
  if (defaultListId) {
    _companies = _getFilteredCompanies(companies, defaultListId);
  }
  return _.orderBy(Object.keys(_.groupBy(_companies, 'country')), [(u) => _.toLower(u)], ['asc']);
};

export const getUniqueIndustries = (companies: ICompanySummary[], defaultListId: number | null): string[] => {
  let _companies = companies;
  if (defaultListId) {
    _companies = _getFilteredCompanies(companies, defaultListId);
  }
  return _.orderBy(Object.keys(_.groupBy(_companies, 'industry_sector')), [(u) => _.toLower(u)], ['asc']);
};

export const areUserListsInCommon = (oneListIds: number[], companies: ICompanySummary[], company_code: string): boolean => {
  const findCompany: ICompanySummary[] = companies.filter((company) => company.company_code === company_code) ?? [];
  if (oneListIds.length === 0 || findCompany.length === 0) {
    return false;
  }

  const secondListIds = getCompanyListIds(findCompany[0]);
  return oneListIds.some((id) => secondListIds.includes(id));
};

const convertToDictionary = (history: ICompany[]): { [key: string]: ITimeseries[] } => {
  const timeSeriesByCompany: { [key: string]: ITimeseries[] } = {};
  history.forEach((company: ICompany) => {
    timeSeriesByCompany[company.company_code] = company.time_series;
  });
  return timeSeriesByCompany;
};

export const addInHistory = (localCompanies: ICompany[], history: ICompany[]) => {
  const historyByCompany = convertToDictionary(history);
  const localCompaniesWithHistory = localCompanies.map((company) => {
    company.time_series = historyByCompany[company.company_code] ?? [];
    return company;
  });
  return localCompaniesWithHistory;
};

export function compareNames(stringA: string, stringB: string, sortDirection: string): number {
  if (!stringA || !stringB) {
    return -1;
  }

  if (sortDirection === 'DOWN') {
    return stringB.localeCompare(stringA);
  }
  return stringA.localeCompare(stringB);
}
